/**
 * Opening another site's window from a tap, on a phone as well as a computer (#7147).
 *
 * A browser lets a tap open a window only for a moment after it: Safari on iOS about a second, Chrome
 * about five. A launch that needs a server round trip first opens its window at once, on a holding page,
 * and sends it to its address when the address arrives.
 *
 * The window is opened without noopener, because noopener makes window.open return null even when the
 * window opens, and then a blocked window can't be told from an open one. Its opener is cleared by hand
 * instead, while it is still a blank page of Fresh's.
 */

/**
 * Whether Fresh is running from a home-screen shortcut, with no browser controls
 * @returns {boolean}
 */
export const isHomeScreenApp = () => window.matchMedia?.('(display-mode: standalone)').matches === true || window.navigator?.standalone === true;

// An iOS home-screen app, the one place a held window can't be sent on (see startLaunchWindow). Only iOS sets
// navigator.standalone; a desktop browser's app window is also display-mode standalone, but launches as a tab does.
const isIosHomeScreenApp = () => window.navigator?.standalone === true;

/**
 * window.open features for a desktop window offset from Fresh's; a phone ignores them and opens a tab
 * @param {Object} [size]
 * @returns {string}
 */
export const popupFeatures = ({ width = 1200, height = 800 } = {}) => `width=${width},height=${height},left=${window.screenX + 50},top=${window.screenY + 50}`;

// A blank window with no way back to Fresh, or null when the browser blocked it
const openBlankWindow = (features) => {
    let launchWindow = null;
    try {
        launchWindow = window.open('', '_blank', features);
    } catch {
        // Some browsers throw for a blocked window rather than returning null
    }
    if (!launchWindow) return null;

    try {
        launchWindow.opener = null;
    } catch {
        // A blank window is Fresh's own, so this only fails in a browser that has already cut the link
    }
    return launchWindow;
};

// What the window shows while its address is on the way. Built with the DOM rather than document.write,
// so the page takes no markup; the viewport keeps it readable on a phone, where it fills the screen.
const showHoldingPage = (launchWindow, message) => {
    try {
        const page = launchWindow.document;
        const viewport = page.createElement('meta');
        viewport.name = 'viewport';
        viewport.content = 'width=device-width, initial-scale=1';
        page.head.append(viewport);
        page.title = message;
        page.body.style.cssText = 'margin:0;height:100vh;display:flex;align-items:center;justify-content:center;font:20px sans-serif';
        page.body.textContent = message;
    } catch {
        // The holding page is a courtesy; the launch works without it
    }
};

/**
 * Start a launch from a tap, and send it to its address once that is known. Call it before the tap's
 * first await, or a phone blocks the window.
 *
 * An iOS home-screen app gets no holding window. iOS opens a home-screen app's windows in a separate
 * browser sheet, and a sheet opened blank can't be sent anywhere afterward, so open() answers 'deferred'
 * and the caller offers the address for a second tap instead.
 *
 * @param {Object} [options]
 * @param {string} [options.message] - What the holding page says
 * @param {string} [options.features] - window.open features for a desktop window
 * @returns {{ open: (url: string) => 'opened'|'blocked'|'closed'|'deferred', cancel: () => void }}
 *   open() sends the window to url; cancel() closes the window when open() was never called
 */
export const startLaunchWindow = ({ message = 'Loading…', features } = {}) => {
    if (isIosHomeScreenApp()) return { open: () => 'deferred', cancel: () => {} };

    const launchWindow = openBlankWindow(features);
    if (launchWindow) showHoldingPage(launchWindow, message);
    let settled = false;

    return {
        open(url) {
            settled = true;
            if (!launchWindow) return 'blocked';
            if (launchWindow.closed) return 'closed';
            // replace, so the window's back button doesn't return to the holding page
            launchWindow.location.replace(url);
            return 'opened';
        },
        cancel() {
            if (settled || !launchWindow) return;
            settled = true;
            try {
                launchWindow.close();
            } catch {
                // Already gone
            }
        },
    };
};

/**
 * Open a launch whose address is already known, straight from a tap
 * An iOS home-screen app's window can't be checked, so it is reported as opened.
 * @param {string} url
 * @param {string} [features] - window.open features for a desktop window
 * @returns {'opened'|'blocked'}
 */
export const openLaunchWindow = (url, features) => {
    if (isIosHomeScreenApp()) {
        window.open(url, '_blank', 'noopener,noreferrer');
        return 'opened';
    }

    const launchWindow = openBlankWindow(features);
    if (!launchWindow) return 'blocked';
    launchWindow.location.replace(url);
    return 'opened';
};
